import React, { useEffect, useMemo, useState } from 'react';
import { VStack, HStack, Flex, Text, Box, Spinner, Center, useToast } from '@chakra-ui/react';
import { FiArrowDownLeft, FiCreditCard, FiSmartphone, FiCheckCircle, FiClock, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { MdOutlineAttachMoney } from 'react-icons/md';
import { api } from 'api/api';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

const tipoConsultaMap = {
  telemedicina_clinico_geral: 'Telemedicina Clínico Geral',
  telemedicine_deovita_pet: 'Telemedicina Veterinário',
  telemedicina_pediatra: 'Telemedicina Pediatra',
  credito: 'Crédito',
};

const TODOS = 'todos';
const ITENS_POR_PAGINA = 10;

function rotuloTipo(tipo) {
  return tipoConsultaMap[tipo] || tipo;
}

function formatarData(data) {
  if (!data) return '-';
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
}

function formatarValor(valor) {
  return `R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function normalizarFormaPagamento(pagamento) {
  const texto = (pagamento.forma_pagamento || '').toLowerCase();

  // Prioriza o texto real da forma de pagamento (fonte da verdade) — "cart" cobre
  // "cartão"/"cartao" (PT) e "card" cobre "credit_card"/"debit_card" (gateways em inglês).
  if (texto.includes('pix')) {
    return { chave: 'pix', label: 'Pix', automatico: false };
  }
  if (texto.includes('cart') || texto.includes('card')) {
    return { chave: 'cartao', label: 'Cartão', automatico: true };
  }

  // split_status só é usado como fallback quando a forma de pagamento não veio preenchida.
  if (pagamento.split_status === 'automatico') {
    return { chave: 'cartao', label: 'Cartão', automatico: true };
  }
  if (pagamento.split_status === 'revisar_manualmente') {
    return { chave: 'pix', label: 'Pix', automatico: false };
  }

  return { chave: 'outro', label: pagamento.forma_pagamento || 'Não identificada', automatico: false };
}

const FORMA_STYLE = {
  cartao: { label: 'Cartão', icon: FiCreditCard, bg: '#E8F5F3', color: '#1F7D75', badgeBg: '#DCFCE7', badgeColor: '#16A34A' },
  pix: { label: 'Pix', icon: FiSmartphone, bg: '#FEF3E2', color: '#B45309', badgeBg: '#FEF3E2', badgeColor: '#B45309' },
  outro: { label: 'Outra', icon: FiCreditCard, bg: '#F1F2F4', color: '#5B6B7A', badgeBg: '#F1F2F4', badgeColor: '#5B6B7A' },
};

function FiltroChips({ opcoes, valorAtivo, setValor }) {
  return (
    <Box overflowX="auto" w="100%" sx={{ '::-webkit-scrollbar': { display: 'none' } }}>
      <HStack spacing="8px" w="max-content" pb="2px">
        {opcoes.map(({ valor, label }) => {
          const ativo = valorAtivo === valor;
          return (
            <Box
              as="button"
              key={valor}
              onClick={() => setValor(valor)}
              bg={ativo ? '#1F7D75' : 'white'}
              color={ativo ? 'white' : '#5B6B7A'}
              border="1px solid"
              borderColor={ativo ? '#1F7D75' : '#EEF1F5'}
              borderRadius="999px"
              px="14px"
              py="9px"
              fontSize="12px"
              fontWeight="600"
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {label}
            </Box>
          );
        })}
      </HStack>
    </Box>
  );
}

function FormaPagamentoTile({ chave, quantidade, total }) {
  const style = FORMA_STYLE[chave];
  const Icon = style.icon;
  const automatico = chave === 'cartao';

  return (
    <Box bg="white" borderRadius="16px" border="1px solid #EEF1F5" p="14px" flex={1} minW={0}>
      <HStack spacing="8px" mb="8px">
        <Flex bg={style.bg} borderRadius="10px" w="32px" h="32px" alignItems="center" justifyContent="center" flexShrink={0}>
          <Icon color={style.color} size={16} />
        </Flex>
        <Text color="#0F1B2A" fontSize="13px" fontWeight="700">
          {style.label}
        </Text>
      </HStack>
      <Text color="#0F1B2A" fontSize="17px" fontWeight="700" noOfLines={1}>
        {formatarValor(total)}
      </Text>
      <HStack spacing="4px" mt="4px">
        {automatico ? <FiCheckCircle color="#16A34A" size={12} /> : <FiClock color="#B45309" size={12} />}
        <Text color={automatico ? '#16A34A' : '#B45309'} fontSize="11px" fontWeight="600">
          {quantidade} {quantidade === 1 ? 'pagamento' : 'pagamentos'} · {automatico ? 'repasse automático' : 'repasse manual'}
        </Text>
      </HStack>
    </Box>
  );
}

function ResumoCard({ total, resumoFormas }) {
  const mesAtual = new Date().toLocaleString('pt-BR', { month: 'long' });

  return (
    <VStack spacing="14px" w="100%" alignItems="flex-start">
      <Box bgGradient="linear(135deg, #2BA89E, #1F7D75)" borderRadius="20px" p="18px" w="100%">
        <HStack spacing="8px">
          <FiArrowDownLeft color="rgba(255,255,255,0.8)" size={16} />
          <Text color="rgba(255,255,255,0.8)" fontSize="13px" fontWeight="500">
            Seu split em {mesAtual}
          </Text>
        </HStack>

        <HStack spacing="6px" alignItems="flex-end" mt="8px">
          <Text color="rgba(255,255,255,0.8)" fontSize="16px" fontWeight="600" mb="2px">
            R$
          </Text>
          <Text color="white" fontSize="30px" fontWeight="700" lineHeight="1">
            {Number(total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        </HStack>
        <Text color="rgba(255,255,255,0.8)" fontSize="12px" fontWeight="500" mt="6px">
          Cartão cai automaticamente. Pix precisa de repasse manual — entre em contato.
        </Text>
      </Box>

      {resumoFormas.length > 0 && (
        <HStack spacing="10px" w="100%" alignItems="stretch">
          {resumoFormas.map((item) => (
            <FormaPagamentoTile key={item.chave} chave={item.chave} quantidade={item.quantidade} total={item.total} />
          ))}
        </HStack>
      )}
    </VStack>
  );
}

function Paginacao({ pagina, totalPaginas, setPagina }) {
  if (totalPaginas <= 1) return null;

  return (
    <HStack justify="space-between" alignItems="center" w="100%" pt="2px">
      <Box
        as="button"
        onClick={() => setPagina((p) => Math.max(1, p - 1))}
        disabled={pagina === 1}
        opacity={pagina === 1 ? 0.4 : 1}
        pointerEvents={pagina === 1 ? 'none' : 'auto'}
        bg="white"
        border="1px solid #EEF1F5"
        borderRadius="12px"
        w="40px"
        h="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <FiChevronLeft color="#1F7D75" size={18} />
      </Box>

      <Text fontSize="12px" color="#5B6B7A" fontWeight="600">
        Página {pagina} de {totalPaginas}
      </Text>

      <Box
        as="button"
        onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
        disabled={pagina === totalPaginas}
        opacity={pagina === totalPaginas ? 0.4 : 1}
        pointerEvents={pagina === totalPaginas ? 'none' : 'auto'}
        bg="white"
        border="1px solid #EEF1F5"
        borderRadius="12px"
        w="40px"
        h="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <FiChevronRight color="#1F7D75" size={18} />
      </Box>
    </HStack>
  );
}

function PagamentosList({ pagamentos, total, filtroAtivo }) {
  return (
    <VStack spacing="14px" w="100%" alignItems="flex-start">
      <Flex justifyContent="space-between" alignItems="center" w="100%">
        <Text color="#0F1B2A" fontSize="17px" fontWeight="700">
          Pagamentos particulares
        </Text>
        <Text color="#9AA5B1" fontSize="12px" fontWeight="500">
          {total} {total === 1 ? 'pagamento' : 'pagamentos'}
        </Text>
      </Flex>

      {pagamentos.length === 0 ? (
        <Text color="#9AA5B1" fontSize="13px" textAlign="center" w="100%" py="8px">
          {filtroAtivo ? 'Nenhum pagamento encontrado para esse filtro.' : 'Nenhuma consulta particular registrada ainda.'}
        </Text>
      ) : (
        <Box bg="white" borderRadius="18px" border="1px solid #EEF1F5" w="100%" overflow="hidden" p="6px">
          {pagamentos.map((pagamento, i) => {
            const forma = normalizarFormaPagamento(pagamento);
            const style = FORMA_STYLE[forma.chave];
            return (
              <React.Fragment key={`${pagamento.transaction_id || pagamento.data_pagamento}-${i}`}>
                {i > 0 && <Box h="1px" bg="#EEF1F5" mx="10px" />}
                <Flex alignItems="center" gap="12px" px="10px" py="12px">
                  <Flex bg="#DCFCE7" borderRadius="12px" w="40px" h="40px" alignItems="center" justifyContent="center" flexShrink={0}>
                    <MdOutlineAttachMoney color="#16A34A" size={20} />
                  </Flex>

                  <VStack spacing="2px" alignItems="flex-start" flex={1} minW={0}>
                    <Text color="#0F1B2A" fontSize="13px" fontWeight="600" noOfLines={1}>
                      {rotuloTipo(pagamento.tipo_credito)}
                    </Text>
                    <Text color="#5B6B7A" fontSize="11px" fontWeight="500" noOfLines={1}>
                      Pago em {formatarData(pagamento.data_pagamento)}
                    </Text>
                  </VStack>

                  <VStack spacing="4px" alignItems="flex-end" flexShrink={0}>
                    <Text color="#16A34A" fontSize="13px" fontWeight="700">
                      {formatarValor(pagamento.valor)}
                    </Text>
                    <HStack
                      spacing="4px"
                      bg={style.badgeBg}
                      borderRadius="999px"
                      px="8px"
                      py="2px"
                    >
                      <style.icon color={style.badgeColor} size={11} />
                      <Text color={style.badgeColor} fontSize="10px" fontWeight="700">
                        {forma.label}
                      </Text>
                    </HStack>
                  </VStack>
                </Flex>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </VStack>
  );
}

function Consultas({ vendedorId }) {
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState(TODOS);
  const [filtroForma, setFiltroForma] = useState(TODOS);
  const [pagina, setPagina] = useState(1);
  const toast = useToast();

  useEffect(() => {
    const fetchConsultas = async () => {
      try {
        setLoading(true);
        const url = `${ENDPOINT}/entrepreneur/extrato/consultas-particulares/${vendedorId}`;
        const response = await api(url, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error('Erro ao buscar consultas');
        }

        const fetchData = await response.json();
        setPagamentos(fetchData.data?.pagamentos || []);
        setError(null);
      } catch (err) {
        console.error('Erro ao buscar consultas:', err);
        setError(err.message);
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar as consultas',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    if (vendedorId) {
      fetchConsultas();
    }
  }, [vendedorId]);

  const tipos = useMemo(
    () => Array.from(new Set(pagamentos.map((p) => p.tipo_credito))).sort(),
    [pagamentos],
  );

  const pagamentosComForma = useMemo(
    () => pagamentos.map((p) => ({ ...p, _forma: normalizarFormaPagamento(p) })),
    [pagamentos],
  );

  const pagamentosFiltrados = useMemo(
    () =>
      pagamentosComForma
        .filter((p) => filtroTipo === TODOS || p.tipo_credito === filtroTipo)
        .filter((p) => filtroForma === TODOS || p._forma.chave === filtroForma),
    [pagamentosComForma, filtroTipo, filtroForma],
  );

  useEffect(() => {
    setPagina(1);
  }, [filtroTipo, filtroForma, pagamentos]);

  const totalPaginas = Math.max(1, Math.ceil(pagamentosFiltrados.length / ITENS_POR_PAGINA));

  const pagamentosPaginados = useMemo(
    () => pagamentosFiltrados.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA),
    [pagamentosFiltrados, pagina],
  );

  const { totalArrecadado, resumoFormas } = useMemo(() => {
    const porForma = new Map();
    let soma = 0;
    pagamentosFiltrados.forEach((p) => {
      soma += p.valor;
      const chave = p._forma.chave;
      if (!porForma.has(chave)) porForma.set(chave, { chave, quantidade: 0, total: 0 });
      const entrada = porForma.get(chave);
      entrada.quantidade += 1;
      entrada.total += p.valor;
    });
    const ordem = { cartao: 0, pix: 1, outro: 2 };
    return {
      totalArrecadado: soma,
      resumoFormas: Array.from(porForma.values()).sort((a, b) => ordem[a.chave] - ordem[b.chave]),
    };
  }, [pagamentosFiltrados]);

  const formasDisponiveis = useMemo(() => {
    const chaves = Array.from(new Set(pagamentosComForma.map((p) => p._forma.chave)));
    const ordem = { cartao: 0, pix: 1, outro: 2 };
    return chaves
      .sort((a, b) => ordem[a] - ordem[b])
      .map((chave) => ({ valor: chave, label: FORMA_STYLE[chave].label }));
  }, [pagamentosComForma]);

  if (loading) {
    return (
      <Center minH="400px">
        <Spinner size="lg" color="#1F7D75" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="400px">
        <Text color="red.500">Erro ao carregar as consultas</Text>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing="18px" w="100%">
      <Box>
        <Text fontSize="22px" fontWeight="700" color="#0F1B2A" mb="2px">
          Repasse de Consultas Particulares
        </Text>
        <Text fontSize="13px" color="#5B6B7A">
          Pagamentos de clientes sem assinatura ativa (no momento do pagamento) ou que optaram por pagar avulso a partir do mês de julho de 2026.
        </Text>
      </Box>

      <ResumoCard total={totalArrecadado} resumoFormas={resumoFormas} />

      {tipos.length > 1 && (
        <FiltroChips
          opcoes={[{ valor: TODOS, label: 'Todos os tipos' }, ...tipos.map((t) => ({ valor: t, label: rotuloTipo(t) }))]}
          valorAtivo={filtroTipo}
          setValor={setFiltroTipo}
        />
      )}

      {formasDisponiveis.length > 1 && (
        <FiltroChips
          opcoes={[{ valor: TODOS, label: 'Todas as formas' }, ...formasDisponiveis]}
          valorAtivo={filtroForma}
          setValor={setFiltroForma}
        />
      )}

      <PagamentosList
        pagamentos={pagamentosPaginados}
        total={pagamentosFiltrados.length}
        filtroAtivo={filtroTipo !== TODOS || filtroForma !== TODOS}
      />

      <Paginacao pagina={pagina} totalPaginas={totalPaginas} setPagina={setPagina} />
    </VStack>
  );
}

export default Consultas;
